import { useState } from "react";
import { ClaimHelper, StringHelper } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { FormDto, QuestionOptionDto } from "../../../services/dto";
import * as yup from "yup";
import { QuestionnaireType } from "./questionnaireContainer";
import { FormikProps } from "formik";
import QuestionOption from "../question/questionOption";
import { QuestionTypes } from "../question/questionForm";
import { useParams } from "react-router-dom";
import { useCommunity } from "../../../context/communityContext";
import { roles } from "../../../assets/configuration";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";

export enum Modules {
    Pophub = '014E9B17-C810-F011-8EF5-00155D112802',
}
export enum FeedbackRatingRanges {
    Min = '1',
    Max = '5'
}
export interface QuestionnaireFormLookupProps {
    community: DropdownOption[];
    userCommunities: DropdownOption[];
    tags: DropdownOption[];
    questionnaireLookupValueType: DropdownOption[];
    questionTypes?: DropdownOption[];
    pollQuestionTypes?: DropdownOption[];
    surveyQuestionTypes?: DropdownOption[];
    pageLink?: DropdownOption[];
    notificationType?: DropdownOption[];
}
interface QuestionnaireFormProps {
    data?: FormDto | undefined;
    show: boolean;
    lookups: QuestionnaireFormLookupProps | undefined;
    onHide: () => void;
    onSave: (values: FormDto) => DrawerFormSaveResult;
    questionnaireType?: string | undefined;
}

export interface PollQuestionniareFormDto {
    id: string;
    name?: string | undefined;
    description?: string | undefined;
    communityId: string;
    typeId: string;
    activeFromDate: Date;
    activeToDate: Date;
    tagId?: string | undefined;
    isDeleted: boolean;
    questionId?: string;
    questionTypeId: string;
    questionName?: string | undefined;
    questionDescription?: string | undefined;
    questionOrder: number;
    startDate: Date;
    options: QuestionOptionDto[] | undefined;
}

const validationSchema = (questionnaire: FormDto | undefined) =>
    yup.object().shape({
        name: yup.string().required("Name is required."),
        communityId: yup.string().required("Community is required.").test("not-empty", "Community is required", (value) => value !== Constants.emptyGuid),
        activeFromDate: yup
            .date()
            .typeError("Please enter a valid date.")
            .nullable()
            .required("Active from date is required."),
        activeToDate: yup
            .date()
            .typeError("Please enter a valid date.")
            .required("Active to date is required.")
            .test({
                name: "Is active to date greater",
                exclusive: false,
                params: {},
                message: "Active to date must be after active from date",
                test: function (value) {
                    if (value) {
                        return value >= this.parent.activeFromDate;
                    }
                    return true;
                },
            }),
        questionName: yup.string().test(
            'name-required',
            'Question is required',
            function (value) {
                const { typeId } = this.parent;
                if ((typeId === QuestionnaireType.Poll.toLowerCase() || typeId === QuestionnaireType.Feedback.toLowerCase()) && !questionnaire || (questionnaire && questionnaire.id == Constants.emptyGuid)) {
                    return !!value;
                }
                return true;
            }
        ),

        questionTypeId: yup.string().test(
            'questonType-required',
            'Question type is required',
            function (value) {
                const { typeId } = this.parent;
                if (typeId === QuestionnaireType.Poll.toLowerCase() && !questionnaire || (questionnaire && questionnaire.id == Constants.emptyGuid)) {
                    return !!value;
                }
                return true;
            }
        ),
    });

const getQuestionnaireTypeLabel = (id?: string, prefix?: string): string => {
    if (id === QuestionnaireType.Poll) return prefix + "Poll";
    if (id === QuestionnaireType.Feedback) return prefix + "Feedback";
    if (id === QuestionnaireType.Survey) return prefix + "Survey";
    return prefix + "Questionnaire";
};

export const QuestionnaireForm = (props: QuestionnaireFormProps) => {
    const { communityId: paramCommunityId } = useParams();
    const { communityId: contextCommunityId } = useCommunity();
    const communityId = props?.data?.communityId || paramCommunityId || contextCommunityId;
    let title: string = getQuestionnaireTypeLabel(props.questionnaireType, "Edit ");
    let questionnaire: FormDto | undefined = props?.data;
    const [isPollType, setIsPollType] = useState<boolean>(props.questionnaireType == QuestionnaireType.Poll.toLowerCase() || props?.data?.typeId == QuestionnaireType.Poll.toLowerCase());
    const [isFeedBackType, setFeedBackType] = useState<boolean>(props.questionnaireType == QuestionnaireType.Feedback.toLowerCase() || props?.data?.typeId == QuestionnaireType.Feedback.toLowerCase());
    if (
        StringHelper.whenNullOrEmpty(questionnaire?.id, Constants.emptyGuid) ===
        Constants.emptyGuid
    ) {
        title = getQuestionnaireTypeLabel(props.questionnaireType, "New ");
    }
    const isSystemAdmin = ClaimHelper.hasRole([roles.SystemAdmin]);

    const onSubmit = (data: PollQuestionniareFormDto) => {
        const formData: FormDto = {
            id: data.id,
            name: data.name,
            description: data.name,
            activeFromDate: data.activeFromDate,
            typeId: data.typeId ?? QuestionnaireType.Survey,
            activeToDate: data.activeToDate,
            communityId: data.communityId,
            tagId: data.tagId,
            moduleId: Constants.emptyGuid,
            statusId: Constants.emptyGuid,
            isDeleted: false
        };
        formData.tagId = (!isPollType && !isFeedBackType) || data.tagId == "" ? undefined : data.tagId;
        formData.moduleId = data.typeId == QuestionnaireType.Poll.toLowerCase() || data.typeId == QuestionnaireType.Feedback.toLowerCase() ? Modules.Pophub : undefined;

        if (props.data?.pollQuestion?.id && props.data?.pollQuestion?.id != Constants.emptyGuid) {
            formData.pollQuestion = { ...props.data?.pollQuestion };
            formData.pollQuestion.name = data.questionName;
            formData.pollQuestion.description = data.questionName;
            formData.pollQuestion.options = isFeedBackType ? undefined : [...data.options!];
            formData.pollQuestion.startDate = data.activeFromDate,
                formData.pollQuestion.endDate = data.activeToDate
        } else {
            if ((isFeedBackType || isPollType) && data?.questionName) {
                formData.pollQuestion = {
                    id: data.questionId ?? Constants.emptyGuid,
                    name: data.questionName,
                    description: data.questionName,
                    questionTypeId: isFeedBackType ? QuestionTypes.Numerical : data.questionTypeId,
                    rangeMinValue: isFeedBackType ? FeedbackRatingRanges.Min : undefined,
                    rangeMaxValue: isFeedBackType ? FeedbackRatingRanges.Max : undefined,
                    startDate: data.activeFromDate,
                    endDate: data.activeToDate,
                    questionOrder: 1,
                    sectionId: Constants.emptyGuid,
                    isRequired: true,
                    isDeleted: false,
                    options: isFeedBackType ? undefined : [...data.options!]
                }
            }
        }
        return Promise.resolve(props?.onSave(formData)).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };

    const isNew = !questionnaire?.id || questionnaire?.id === Constants.emptyGuid;
    const isLoading = props?.show && !isNew && !questionnaire;
    const questionnaireTypelookups = props.questionnaireType && props.questionnaireType != Constants.emptyGuid ?
        props?.lookups?.questionnaireLookupValueType.filter(x => x.key == props.questionnaireType) : props?.lookups?.questionnaireLookupValueType;
    const pollQuestionTypeOptions =
        props.lookups?.pollQuestionTypes?.length
            ? props.lookups.pollQuestionTypes
            : props.lookups?.questionTypes;

    return (
        <Drawer.Form
            className="w-30"
            show={props?.show}
            backdrop={true}
            placement="end"
            scroll={true}
            onHide={props?.onHide}
            title={title}
            initialValues={{
                ...questionnaire, communityId: communityId,
                typeId: props.questionnaireType,
                questionId: props.data?.pollQuestion?.id,
                questionName: props.data?.pollQuestion?.name,
                questionTypeId: props.data?.pollQuestion?.questionTypeId ?? undefined,
                options: props.data?.pollQuestion?.options ?? undefined
            }}
            validationSchema={validationSchema(questionnaire)}
            on_Submit={onSubmit}
        >
            {(formikProps: FormikProps<any>) => {

                return (
                    <>
                        <Form.PanelSection iconClassName="bi bi-info-circle" title="1. Basic Information">
                            <Form.Text name="name" label="Title" isLoading={isLoading} />
                            {(isPollType || isFeedBackType) &&
                                <Form.TextArea
                                    name="questionName"
                                    rows={3}
                                    label="Question"
                                    isLoading={isLoading}
                                />
                            }
                            {!(isPollType || isFeedBackType) &&
                                <Form.TextArea
                                    name="description"
                                    rows={3}
                                    label="Description"
                                    isLoading={isLoading}
                                />
                            }
                            <Form.Dropdown
                                name="typeId"
                                label="Questionnaire Type"
                                isLoading={isLoading}
                                options={questionnaireTypelookups}
                                blankOption={{
                                    key: Constants.emptyGuid,
                                    display: "Select Type",
                                    value: Constants.emptyString,
                                }}
                                onChange={(e: any) => { setIsPollType(e.target.value == QuestionnaireType.Poll.toLowerCase()); setFeedBackType(e.target.value == QuestionnaireType.Feedback.toLowerCase()); }}
                                isHidden={true}
                            />
                            {(isPollType || isFeedBackType) &&
                                <Form.Dropdown
                                    name="tagId"
                                    label="Tag"
                                    isLoading={isLoading}
                                    options={props?.lookups?.tags}
                                    blankOption={{
                                        key: Constants.emptyGuid,
                                        display: "Select Tag",
                                        value: Constants.emptyString,
                                    }}
                                    isHidden={true}
                                />
                            }
                        </Form.PanelSection>

                        <Form.PanelSection
                            iconClassName="bi bi-calendar-range"
                            title="2. Activation Dates"
                            style={{ marginTop: 16 }}
                        >
                            <Form.Date
                                name="activeFromDate"
                                label="From Date"
                                isLoading={isLoading}
                                placeholder="Enter date"
                            />
                            <Form.Date
                                name="activeToDate"
                                label="To Date"
                                isLoading={isLoading}
                                placeholder="Enter date"
                                useStartDateForMin="activeFromDate"
                            />
                        </Form.PanelSection>

                        {(isPollType || isFeedBackType) &&
                            <>
                                {
                                    <>
                                        {isPollType &&

                                            <Form.PanelSection
                                                iconClassName="bi bi-ui-checks"
                                                title="3. Question Type"
                                                style={{ marginTop: 16 }}
                                            >
                                                <Form.Dropdown
                                                    name="questionTypeId"
                                                    label="Question Type"
                                                    disabled={!isNew}
                                                    isLoading={isLoading}
                                                    blankOption={{
                                                        key: Constants.emptyGuid,
                                                        display: "Select Question Type",
                                                        value: Constants.emptyString,
                                                    }}
                                                    options={pollQuestionTypeOptions}
                                                />
                                            </Form.PanelSection>

                                        }
                                        {formikProps !== null &&
                                            formikProps.values.questionTypeId &&
                                            (formikProps.values.questionTypeId ===
                                                QuestionTypes.Dropdown.toLowerCase() ||
                                                formikProps.values.questionTypeId ===
                                                QuestionTypes.Checkbox.toLowerCase() ||
                                                formikProps.values.questionTypeId ===
                                                QuestionTypes.Radio.toLowerCase()) && (
                                                <QuestionOption
                                                    formikProps={{
                                                        ...formikProps,
                                                        setValues: (values: any) => {
                                                            return formikProps.setValues({ ...formikProps.values, options: formikProps.values.pollQuestion?.options ?? [], });
                                                        }
                                                    }}
                                                    title={isPollType ? "4. Options" : "3. Options"}
                                                    style={{ marginTop: 16 }}
                                                />
                                            )}
                                    </>

                                }
                            </>
                        }
                    </>
                );
            }}
        </Drawer.Form>
    );
};